import {
  AccountTemplateEnum,
  AccountTemplateScopeEnum,
  IAccountOrgIdTemplate,
  TEMPLATES_ORGIDS_ALL,
} from '@share/common-types';

export interface IResolvedAccountTemplate {
  accountTemplate?: AccountTemplateEnum;
  isNewTemplate: boolean;
}

// Decides whether a per-org template entry applies on the current page given its scope:
//  FULL (or empty/null/undefined) - every page
//  POST_SEARCH                     - every page except the search/result page
//  SEARCH_ONLY                     - only the search/result page
//  NONE                            - never
export const shouldApplyTemplateScope = (
  scope: AccountTemplateScopeEnum | string | null | undefined,
  isSearchPage: boolean,
): boolean => {
  switch (scope) {
    case AccountTemplateScopeEnum.PostSearch:
      return !isSearchPage;
    case AccountTemplateScopeEnum.SearchOnly:
      return isSearchPage;
    case AccountTemplateScopeEnum.None:
      return false;
    case AccountTemplateScopeEnum.Full:
    default:
      return true;
  }
};

const isAllEntry = (entry: IAccountOrgIdTemplate): boolean =>
  entry?.organizationId?.toString().toUpperCase() === TEMPLATES_ORGIDS_ALL;

// Resolves the template for an organization applying the two backoffice rules:
//  - 'ALL' is a fallback: a specific organizationId entry always wins over 'ALL', and
//    'ALL' only applies to organizations that have no specific entry of their own.
//  - scope decides whether the resolved template applies on the current page.
// `base` is the account-level default returned when there is no per-org list or org id.
export const resolveOrgTemplate = (
  templatesOrgIdsList: IAccountOrgIdTemplate[] | null | undefined,
  organizationId: number | string | null | undefined,
  isSearchPage: boolean,
  base: IResolvedAccountTemplate,
): IResolvedAccountTemplate => {
  if (organizationId == null || !templatesOrgIdsList?.length) {
    return base;
  }

  const orgIdStr = organizationId.toString();
  const specificEntry = templatesOrgIdsList.find(
    (entry) => !isAllEntry(entry) && entry?.organizationId?.toString() === orgIdStr,
  );
  const entry = specificEntry || templatesOrgIdsList.find(isAllEntry);

  if (!entry || !shouldApplyTemplateScope(entry.scope, isSearchPage)) {
    return { accountTemplate: undefined, isNewTemplate: false };
  }

  return {
    accountTemplate: entry.template as AccountTemplateEnum,
    isNewTemplate: entry.template === AccountTemplateEnum.NewTemplate,
  };
};
